export type DocumentTemplateScope = 'available' | 'mine' | 'recent' | 'enterprise' | 'project';

export type DocumentTemplateType = 'report' | 'sor' | 'meeting' | 'blank' | 'requirement' | 'review' | string;

export interface DocumentTemplatePreviewSection {
  title: string;
  content: string;
  highlighted?: boolean;
}

export interface DocumentTemplate {
  id: string;
  title: string;
  type: DocumentTemplateType;
  version: string;
  status?: DocumentSkillStatus | string;
  skillCount: number;
  scope: DocumentTemplateScope;
  featured?: boolean;
  previewTitle: string;
  previewSections: DocumentTemplatePreviewSection[];
  previewImageUrl?: string;
  updatedAt?: string;
  description?: string;
  creatorUserId?: string;
  creatorName?: string;
  canManage?: boolean;
  canPublish?: boolean;
  canDisable?: boolean;
  canArchive?: boolean;
  canDelete?: boolean;
  /** 项目模版所属项目 */
  projectId?: string;
  /** 项目模版所属项目名称 */
  projectName?: string;
  /** 源文件名（含扩展名），用于展示格式图标 */
  sourceFileName?: string;
}

export interface DocumentTemplateListRequest {
  projectId: string;
  scope: DocumentTemplateScope;
  keyword?: string;
  status?: string;
  documentType?: string;
  /** 按所属项目筛选（项目模版） */
  filterProjectId?: string | number;
  sort?: string;
  creatorUserId?: string | number;
}

export interface DocumentTemplateTabCounts {
  available: number;
  enterprise: number;
  project: number;
  mine: number;
}

export interface DocumentTemplateListResponse {
  templates: DocumentTemplate[];
  tabCounts?: DocumentTemplateTabCounts;
  canCreateBlankDocument: boolean;
  canCreateMineTemplate?: boolean;
  /** 项目成员可创建项目模版（需指定具体项目） */
  canCreateProjectTemplates?: boolean;
  /** 项目负责人/创建人/系统管理员可管理他人项目模版 */
  canManageProjectTemplates?: boolean;
  /** 仅系统管理员可创建企业模版 */
  canCreateEnterpriseTemplates?: boolean;
  canManageEnterpriseTemplates?: boolean;
}

export interface CreateDocumentTemplateSectionInput {
  title: string;
  description?: string;
}

export interface CreateDocumentTemplateRequest {
  title: string;
  documentType: string;
  description: string;
  usageScenario?: string;
  saveScope: 'mine' | 'project' | 'enterprise';
  /** 保存为项目模版时必填 */
  projectId?: number | string;
  sections?: CreateDocumentTemplateSectionInput[];
  selectedSkillIds?: string[];
}

export type WorkbenchDocumentFileType =
  | 'pdf'
  | 'docx'
  | 'doc'
  | 'pptx'
  | 'ppt'
  | 'xlsx'
  | 'xls'
  | 'html'
  | 'bin'
  | string;

export interface WorkbenchDocumentListItem {
  id: string;
  name: string;
  fileType: WorkbenchDocumentFileType;
  status: 'editable' | 'locked' | 'archived';
  updatedAt: string;
}

export interface WorkbenchDocumentDetail {
  id: string;
  title: string;
  statusLabel: string;
  statusDescription: string;
  contentHtml?: string;
  /** 与 contentHtml 同源的 Markdown，供在线预览 */
  contentMarkdown?: string;
  hasSourceFile?: boolean;
  sourceFileName?: string;
}

export interface WorkbenchDocumentComment {
  id: string;
  excerpt: string;
  author: string;
  time: string;
}

export interface DocumentWorkbenchSnapshot {
  documents: WorkbenchDocumentListItem[];
  activeDocument: WorkbenchDocumentDetail;
  comments: WorkbenchDocumentComment[];
}

export interface CreateDocumentResult {
  documentId: string;
  title: string;
  routeUrl?: string;
  contentUrl?: string;
}

export interface TemplatePreviewResult {
  templateId: string;
  title: string;
  previewHtml?: string;
  previewImageUrl?: string;
  hasSourceFile?: boolean;
  sourceFileName?: string;
}

export interface SaveAsTemplateFormOption {
  label: string;
  value: string;
}

export interface SaveAsTemplateBasicInfo {
  documentId: string;
  sourceDocumentTitle: string;
  templateName: string;
  documentType: string;
  templateDescription: string;
  usageScenario: string;
  documentTypeOptions: SaveAsTemplateFormOption[];
}

export interface SaveAsTemplateStructureItem {
  id: string;
  index: number;
  title: string;
  description: string;
  contentHtml?: string;
  checked: boolean;
  highlighted?: boolean;
  blockKind?: string;
  elementIds?: string[];
  tableCount?: number;
  imageCount?: number;
}

export interface DocumentParseElement {
  id: string;
  type: string;
  index: number;
  level?: number;
  text?: string;
  htmlSnippet?: string;
  rowCount?: number;
  colCount?: number;
  headerCells?: string[];
  imageSrc?: string;
  imageAlt?: string;
  imageWidth?: string;
  imageHeight?: string;
  relatedElementId?: string;
  nearestHeading?: string;
}

export interface TemplateSkeleton {
  headingTree?: Array<{
    elementId: string;
    title: string;
    level: number;
    childElementIds?: string[];
  }>;
  tables?: Array<{
    elementId: string;
    nearestHeading?: string;
    rowCount: number;
    colCount: number;
    headerCells?: string[];
    hasDataRows?: boolean;
    relatedCaptionId?: string;
  }>;
  kvFields?: Array<{
    elementId: string;
    label: string;
    sampleValue?: string;
    nearestHeading?: string;
  }>;
  mediaNodes?: Array<{
    kind: string;
    elementId: string;
    captionElementId?: string;
    captionText?: string;
    imageSrc?: string;
    nearestHeading?: string;
  }>;
}

export interface SaveAsTemplateParseResult {
  documentId: string;
  phase: string;
  elementCount: number;
  headingCount: number;
  paragraphCount: number;
  tableCount: number;
  imageCount: number;
  figureCaptionCount: number;
  tableCaptionCount: number;
  listCount: number;
  elements: DocumentParseElement[];
  summary: string;
}

export interface SaveAsTemplateRecognizeResult {
  documentId: string;
  phase: string;
  skeleton: TemplateSkeleton;
  structure: SaveAsTemplateStructureInfo;
  summary: string;
}

export interface SaveAsTemplateGenerateResult {
  documentId: string;
  phase: string;
  extractSource: string;
  summary: string;
  assistantReply: string;
  templateName?: string;
  documentType?: string;
  templateDescription?: string;
  usageScenario?: string;
  structure: SaveAsTemplateStructureInfo;
  review: SaveAsTemplateReviewInfo;
  templateHtml?: string;
}

export interface SaveAsTemplateStructureInfo {
  documentId: string;
  chapterCount: number;
  tableCount: number;
  placeholderCount: number;
  imageCount?: number;
  skeleton?: TemplateSkeleton;
  items: SaveAsTemplateStructureItem[];
}

export type SaveAsTemplateReviewLevel = 'high' | 'medium' | 'low';

export type SaveAsTemplateReviewAction = 'pending' | 'delete' | 'false-positive' | 'placeholder';

export interface SaveAsTemplateReviewSuggestedAction {
  type: Exclude<SaveAsTemplateReviewAction, 'pending'>;
  label: string;
  placeholderKey?: string;
  placeholderText?: string;
  sensitiveType?: string;
}

export interface SaveAsTemplateReviewItem {
  id: string;
  sourceTitle: string;
  location: string;
  content: string;
  originalContent?: string;
  suggestedAction: SaveAsTemplateReviewSuggestedAction;
  level: SaveAsTemplateReviewLevel;
  action: SaveAsTemplateReviewAction;
}

export interface SaveAsTemplateReviewInfo {
  documentId: string;
  candidateCount: number;
  highRiskCount: number;
  mediumRiskCount: number;
  lowRiskCount: number;
  items: SaveAsTemplateReviewItem[];
}

/** 四阶段编排结果（parse → recognize → generate） */
export interface SaveAsTemplateExtractResult {
  documentId: string;
  /** llm | heuristic */
  extractSource: string;
  summary: string;
  assistantReply: string;
  templateName?: string;
  documentType?: string;
  templateDescription?: string;
  usageScenario?: string;
  chapterTitles?: string[];
  redactionCategories?: string[];
  structure: SaveAsTemplateStructureInfo;
  review: SaveAsTemplateReviewInfo;
  phase?: string;
  parse?: SaveAsTemplateParseResult;
  skeleton?: TemplateSkeleton;
  templateHtml?: string;
}

export type SaveAsTemplateSaveScope = 'mine' | 'project' | 'enterprise';

export interface SaveAsTemplatePreviewSection {
  id: string;
  index: number;
  title: string;
  previewText: string;
  structureItemId?: string;
  placeholderKey?: string;
  placeholderText?: string;
  bindingStatus: 'static' | 'placeholder';
}

export interface SaveAsTemplateSaveScopeOption {
  value: SaveAsTemplateSaveScope;
  label: string;
  description: string;
  enabled: boolean;
  permissionCode?: string;
}

export interface SaveAsTemplateSkillOption {
  id: string;
  code: string;
  name: string;
  version: string;
  category: string;
  description: string;
  checked: boolean;
}

export interface SaveAsTemplatePreviewInfo {
  documentId: string;
  previewSnapshotId: string;
  templateTitle: string;
  sourceDocumentVersion: string;
  generatedAt: string;
  defaultSaveScope: SaveAsTemplateSaveScope;
  /** 高保真 HTML，与保存同源 */
  previewHtml?: string;
  previewSections: SaveAsTemplatePreviewSection[];
  saveScopeOptions: SaveAsTemplateSaveScopeOption[];
  skillOptions: SaveAsTemplateSkillOption[];
}

export interface SaveAsTemplateRequest {
  previewSnapshotId: string;
  templateName: string;
  documentType: string;
  templateDescription: string;
  usageScenario: string;
  saveScope: SaveAsTemplateSaveScope;
  /** 保存为项目模版时必填 */
  projectId?: number | string;
  selectedStructureItemIds: string[];
  reviewActions: Array<{
    reviewItemId: string;
    action: SaveAsTemplateReviewAction;
    placeholderKey?: string;
    placeholderText?: string;
    sensitiveType?: string;
  }>;
  selectedSkillIds: string[];
}

export interface SaveAsTemplateResult {
  templateId: string;
  templateTitle: string;
  saveScope: SaveAsTemplateSaveScope;
  status?: string;
  templateVersion: string;
  createdAt: string;
  requiresPublish?: boolean;
  hasSourceFile?: boolean;
  sourceFileName?: string;
  sourceFormatHint?: string;
  generationMode?: string;
  sopSummary?: string;
  requiredContextLabels?: string[];
}

export interface DocumentTemplatePlaceholder {
  id?: string;
  placeholderKey?: string;
  placeholderText?: string;
  sensitiveType?: string;
  sectionId?: string;
}

export interface TemplateGenerationProfileSummary {
  generationMode?: string;
  sopText?: string;
  requiredContext?: Array<{ key?: string; label?: string; blocking?: boolean; reason?: string }>;
  complexity?: {
    sheetCount?: number;
    tableCount?: number;
    mediaCount?: number;
    imageHeavy?: boolean;
    highFormula?: boolean;
  };
  mediaClipPlan?: {
    applied?: boolean;
    originalMediaCount?: number;
    clippedMediaCount?: number;
  };
  buildNotes?: string;
}

export interface DocumentTemplateDetail {
  id: string;
  title: string;
  documentType?: string;
  description?: string;
  usageScenario?: string;
  version: string;
  status: string;
  saveScope: SaveAsTemplateSaveScope | string;
  contentHtml?: string;
  previewHtml?: string;
  skillCount: number;
  ownerUserId?: string;
  projectId?: string;
  updatedAt?: string;
  createdAt?: string;
  canEdit?: boolean;
  canPublish?: boolean;
  canDisable?: boolean;
  canArchive?: boolean;
  canDelete?: boolean;
  hasSourceFile?: boolean;
  sourceFileName?: string;
  sections?: DocumentTemplatePreviewSection[];
  placeholders?: DocumentTemplatePlaceholder[];
  generationProfile?: TemplateGenerationProfileSummary;
  generationProfileJson?: string;
}

export interface PublishDocumentTemplateRequest {
  targetScope?: SaveAsTemplateSaveScope;
  projectId?: number;
}

export interface ApplyTemplateStructureResult {
  documentId: string;
  templateId: string;
  insertedSectionCount: number;
  contentHtml?: string;
}

export interface TemplateDialogueDraft {
  title?: string;
  documentType?: string;
  description?: string;
  usageScenario?: string;
  outlineChapters?: string[];
  placeholders?: DocumentTemplatePlaceholder[];
  contentHtml?: string;
}

export interface TemplateDialogueRequest {
  conversationId?: string;
  userMessage?: string;
  history?: Array<{ role: string; content: string }>;
  confirmSave?: boolean;
  draft?: TemplateDialogueDraft;
}

export interface TemplateDialogueResult {
  conversationId: string;
  phase: 'discuss' | 'draft_ready' | 'saved' | string;
  assistantReply: string;
  draft?: TemplateDialogueDraft;
  template?: DocumentTemplateDetail;
  warnings?: string[];
}

export interface ImportDocumentTemplateRequest {
  packageContent: string;
  title?: string;
  documentType?: string;
}

export interface ImportDocumentTemplateResult {
  success: boolean;
  template?: DocumentTemplateDetail;
  warnings?: string[];
  fixableErrors?: string[];
}

/** Skill 资产 */

export type DocumentSkillStatus = 'draft' | 'published' | 'disabled' | 'archived';
export type DocumentSkillVisibility = 'private' | 'project' | 'enterprise';

export interface DocumentSkillListItem {
  id: string;
  code: string;
  name: string;
  category: string;
  description: string;
  version: string;
  visibility: DocumentSkillVisibility | string;
  status: DocumentSkillStatus | string;
  updatedAt?: string;
}

export interface DocumentSkillDetail extends Omit<DocumentSkillListItem, 'version'> {
  currentVersion?: string;
  promptText?: string;
  sopText?: string;
  inputSchemaJson?: string;
  toolPermissionsJson?: string;
  outputContractJson?: string;
  /** 是否已保存完整标准包产物 */
  hasStandardPackage?: boolean;
  /** 验收用例数量 */
  acceptanceCaseCount?: number;
  /** 包 schema 版本 */
  packageSchemaVersion?: string;
}

export interface SkillAcceptanceCaseResult {
  id?: string;
  category?: string;
  description?: string;
  status: 'passed' | 'failed' | 'skipped' | string;
  expectedDecisionCode?: string;
  observedDecisionCode?: string;
  detail?: string;
}

export interface SkillAcceptanceRunResult {
  skillId: string;
  provider?: string;
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  cases?: SkillAcceptanceCaseResult[];
  warnings?: string[];
}

export interface CreateDocumentSkillRequest {
  code: string;
  name: string;
  category: string;
  description?: string;
  visibility?: DocumentSkillVisibility | string;
  promptText?: string;
  sopText?: string;
  inputSchemaJson?: string;
  toolPermissionsJson?: string;
  outputContractJson?: string;
}

export interface ImportDocumentSkillRequest {
  sourceSkillId?: string;
  code?: string;
  name?: string;
  /** Skill 包 JSON 文本（兼容旧格式） */
  packageContent?: string;
  /** 标准 Skill ZIP 包（Base64） */
  packageBase64?: string;
}

export interface ImportDocumentSkillResult {
  skill: DocumentSkillDetail;
  warnings?: string[];
  importMode?: 'clone' | 'package' | string;
}

export interface ParseSkillFromDialogueRequest {
  dialogueText: string;
  category?: string;
}

export interface ParseSkillFromDialogueResult {
  code: string;
  name: string;
  category: string;
  description?: string;
  visibility?: string;
  promptText?: string;
  sopText?: string;
  inputSchemaJson?: string;
  toolPermissionsJson?: string;
  outputContractJson?: string;
  parseSource?: 'llm' | 'heuristic' | string;
  warnings?: string[];
}

/** 多轮对话创建 / 优化 Skill */
export type SkillDialogueMode = 'create' | 'optimize';
export type SkillDialoguePhase = 'discuss' | 'draft_ready';

export interface SkillDialogueHistoryItem {
  role: 'user' | 'assistant' | string;
  content: string;
}

export interface SkillDialogueRequest {
  mode: SkillDialogueMode | string;
  conversationId?: string;
  userMessage: string;
  history?: SkillDialogueHistoryItem[];
  sourceSkillId?: string;
  category?: string;
}

export interface SkillDialogueResult {
  conversationId: string;
  phase: SkillDialoguePhase | string;
  assistantReply: string;
  draft?: ParseSkillFromDialogueResult | null;
  warnings?: string[];
}

export interface UpdateDocumentSkillContentRequest {
  name?: string;
  category?: string;
  description?: string;
  promptText?: string;
  sopText?: string;
  inputSchemaJson?: string;
  toolPermissionsJson?: string;
  outputContractJson?: string;
}

/** 助手消息结构化：Skill 草稿预览卡 */
export type SmartDocumentSkillDraftStructured = {
  kind: 'smart_document_skill_draft';
  status: 'pending' | 'saving' | 'done' | 'cancelled';
  mode: SkillDialogueMode | string;
  code?: string;
  name?: string;
  category?: string;
  description?: string;
  promptText?: string;
  sopText?: string;
  inputSchemaJson?: string;
  toolPermissionsJson?: string;
  outputContractJson?: string;
  sourceSkillId?: string;
  warnings?: string[];
};

/** 助手消息结构化：本地 Skill 包导入进度卡 */
export type SmartDocumentSkillImportStructured = {
  kind: 'smart_document_skill_import';
  status: 'parsing' | 'done' | 'failed';
  fileName?: string;
  skillId?: string;
  code?: string;
  name?: string;
  category?: string;
  description?: string;
  hasStandardPackage?: boolean;
  acceptanceCaseCount?: number;
  warnings?: string[];
  errorMessage?: string;
};

/** 助手消息结构化：模版草稿预览卡 */
export type SmartDocumentTemplateDraftStructured = {
  kind: 'smart_document_template_draft';
  status: 'pending' | 'saving' | 'done' | 'cancelled';
  title?: string;
  documentType?: string;
  description?: string;
  usageScenario?: string;
  outlineChapters?: string[];
  contentHtml?: string;
  warnings?: string[];
  templateId?: string;
};

/** 模版引用 AI 多轮生成 */
export type TemplateGenerationStatus =
  | 'collecting'
  | 'generating'
  | 'completed'
  | 'failed'
  | 'opening'
  | 'opened';

export interface StartTemplateGenerationRequest {
  templateId: string;
  skillIds?: string[];
  userMessage?: string;
  sessionId?: string;
  atTokens?: SmartDocAtTokenPayload[];
  attachments?: TemplateGenerationAttachmentPayload[];
}

export interface TemplateGenerationMessageRequest {
  userMessage?: string;
  atTokens?: SmartDocAtTokenPayload[];
  attachments?: TemplateGenerationAttachmentPayload[];
}

export interface SmartDocAtTokenPayload {
  kind: string;
  name: string;
  refId: string;
  label?: string;
}

export interface TemplateGenerationAttachmentPayload {
  name?: string;
  objectKey?: string;
  accessUrl?: string;
  type?: string;
}

export interface TemplateGenerationMatchHit {
  slotKey?: string;
  slotLabel?: string;
  value?: string;
  sourceTitle?: string;
  snippet?: string;
}

export interface TemplateGenerationReference {
  title?: string;
  source?: string;
  snippet?: string;
  documentId?: string;
}

export interface TemplateGenerationProcessStep {
  key: string;
  title?: string;
  status?: string;
  text?: string;
  detail?: Record<string, unknown>;
}

/** 交付后可点击定位的核对清单项 */
export interface TemplateGenerationReviewItem {
  label: string;
  category?: string;
  searchText?: string;
  sectionHint?: string;
  priority?: string;
}

export interface TemplateGenerationResult {
  generationId: string;
  status: TemplateGenerationStatus | string;
  assistantReply?: string;
  missingFields?: string[];
  filledSlots?: Record<string, string>;
  templateId?: string;
  templateTitle?: string;
  resultHtml?: string;
  /** 与下载同源的 Markdown 预览 */
  resultMarkdown?: string;
  documentId?: string;
  documentTitle?: string;
  errorMessage?: string;
  matchHits?: TemplateGenerationMatchHit[];
  referenceSummaries?: TemplateGenerationReference[];
  usedSourceFormat?: boolean;
  processSteps?: TemplateGenerationProcessStep[];
  intentSummary?: string;
  fillInstructions?: string;
  outlineSummary?: string;
  validationIssueCount?: number;
  pendingConfirmCount?: number;
  qualitySummary?: string;
  reviewChecklist?: TemplateGenerationReviewItem[];
  generationMode?: string;
  requiredContextAsked?: boolean;
}

/** 助手消息结构化：模版生成进度卡 */
export type SmartDocumentTemplateGenerateStructured = {
  kind: 'smart_document_template_generate';
  status: TemplateGenerationStatus | string;
  generationId?: string;
  templateId?: string;
  templateTitle?: string;
  missingFields?: string[];
  filledSlots?: Record<string, string>;
  documentId?: string;
  documentTitle?: string;
  errorMessage?: string;
  matchHits?: TemplateGenerationMatchHit[];
  referenceSummaries?: TemplateGenerationReference[];
  usedSourceFormat?: boolean;
  processSteps?: TemplateGenerationProcessStep[];
  intentSummary?: string;
  fillInstructions?: string;
  outlineSummary?: string;
  validationIssueCount?: number;
  pendingConfirmCount?: number;
  qualitySummary?: string;
  reviewChecklist?: TemplateGenerationReviewItem[];
  /** 交付摘要原文，打开文档后仍保留展示 */
  deliverySummary?: string;
};

/** Skill 执行 / Citation / 写回 */

export type SkillExecutionStatus =
  | 'waiting'
  | 'retrieving'
  | 'generating'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface SkillExecutionCitation {
  index: number;
  title: string;
  snippet: string;
  source?: string;
  score?: number;
  documentId?: string;
  location?: string;
}

export interface StartSkillExecutionRequest {
  skillId: string;
  documentId?: string;
  userInput?: string;
  sessionId?: string;
  contextJson?: string;
}

export interface SkillExecutionDetail {
  executionId: string;
  status: SkillExecutionStatus | string;
  skillId?: string;
  documentId?: string;
  sessionId?: string;
  resultHtml?: string;
  /** 模型指定的文档标题（工作台标题 / 导出文件名） */
  resultTitle?: string;
  citations?: SkillExecutionCitation[];
  /** 仍需用户补充的关键问题（多轮追问） */
  followUpQuestions?: string[];
  errorMessage?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApplySkillExecutionRequest {
  documentId?: string;
  createNewDocument?: boolean;
  title?: string;
}

export interface ApplySkillExecutionResult {
  documentId: string;
  title?: string;
  applied?: boolean;
  createdNew?: boolean;
}

export interface ResolveSkillApplyTargetRequest {
  userInput?: string;
  currentDocumentId?: string;
  currentDocumentTitle?: string;
  previousClarifyQuestion?: string;
}

export type SkillApplyTargetDecision = 'create_new' | 'update_current' | 'clarify';

export interface ResolveSkillApplyTargetResult {
  decision: SkillApplyTargetDecision | string;
  confidence?: string;
  clarifyQuestion?: string;
  reason?: string;
}

/** 智能文档页：通用输出意图 */
export type SmartDocOutputIntentDecision =
  | 'create_document'
  | 'merge_current'
  | 'revise_current'
  | 'qa'
  | 'clarify';

export interface SmartDocOutputIntentRequest {
  userMessage?: string;
  hasCurrentDocument?: boolean;
  currentDocumentId?: string;
  currentDocumentTitle?: string;
  previousClarifyQuestion?: string;
  mentionedProjectName?: string;
}

export interface SmartDocOutputIntentResult {
  decision: SmartDocOutputIntentDecision | string;
  confidence?: string;
  clarifyQuestion?: string;
  reason?: string;
  suggestedTitle?: string;
}

/** AI 输入区 @ Token */

export type SmartDocAtKind = 'skill' | 'template' | 'project';

export interface SmartDocAtToken {
  id: string;
  kind: SmartDocAtKind;
  /** 细项名称（技能名 / 模版名 / 项目文档名） */
  name: string;
  /** 统一展示：@技能-SOR生成 */
  label: string;
  refId: string;
}

export function smartDocAtTypeLabel(kind: SmartDocAtKind): string {
  if (kind === 'skill') return '技能';
  if (kind === 'template') return '模板';
  return '项目';
}

/** 引用细项包裹符：避免空格、连字符把层级截断 */
export const SMART_DOC_MENTION_SEG_OPEN = '「';
export const SMART_DOC_MENTION_SEG_CLOSE = '」';

export function wrapSmartDocMentionSegment(raw: string): string {
  const s = String(raw ?? '').replace(/[「」]/g, '').trim();
  return `${SMART_DOC_MENTION_SEG_OPEN}${s}${SMART_DOC_MENTION_SEG_CLOSE}`;
}

/** 单段展示截断：保留头尾（文件尽量留扩展名），避免过长占满输入区 */
export function truncateSmartDocSegment(text: string, maxLen: number): string {
  const s = String(text ?? '').trim();
  if (!s || s.length <= maxLen) return s;
  if (maxLen <= 2) return '…';
  const extMatch = s.match(/(\.[A-Za-z0-9]{1,10})$/);
  const ext = extMatch?.[1] || '';
  const budget = maxLen - 1 - ext.length;
  if (budget <= 2) {
    return `${s.slice(0, Math.max(1, maxLen - 1))}…`;
  }
  const head = Math.max(2, Math.ceil(budget * 0.55));
  const tail = Math.max(0, budget - head);
  const body = ext ? s.slice(0, -ext.length) : s;
  if (tail > 0 && body.length > head + tail) {
    return `${body.slice(0, head)}…${body.slice(-tail)}${ext}`;
  }
  return `${body.slice(0, budget)}…${ext}`;
}

export const SMART_DOC_PROJECT_NAME_DISPLAY_MAX = 12;
export const SMART_DOC_DOC_NAME_DISPLAY_MAX = 28;
export const SMART_DOC_SKILL_NAME_DISPLAY_MAX = 24;

export function buildSmartDocAtLabel(kind: SmartDocAtKind, name: string): string {
  const max = kind === 'project' ? SMART_DOC_DOC_NAME_DISPLAY_MAX : SMART_DOC_SKILL_NAME_DISPLAY_MAX;
  const n = truncateSmartDocSegment(name, max) || smartDocAtTypeLabel(kind);
  return `@${smartDocAtTypeLabel(kind)}-${wrapSmartDocMentionSegment(n)}`;
}

/** 完整项目引用展示：两级都保留，分别截断 */
export function buildSmartDocProjectDisplayLabel(projectName: string, docName: string): string {
  const p = truncateSmartDocSegment(projectName, SMART_DOC_PROJECT_NAME_DISPLAY_MAX) || '项目';
  const d = truncateSmartDocSegment(docName, SMART_DOC_DOC_NAME_DISPLAY_MAX) || '文档';
  return `@项目-${wrapSmartDocMentionSegment(p)}-${wrapSmartDocMentionSegment(d)}`;
}

export function buildSmartDocProjectPrefixLabel(projectName: string): string {
  const p = truncateSmartDocSegment(projectName, SMART_DOC_PROJECT_NAME_DISPLAY_MAX) || '项目';
  return `@项目-${wrapSmartDocMentionSegment(p)}`;
}

/** 项目级引用（全库检索）：@项目-「项目名」 */
export function buildSmartDocProjectOnlyLabel(projectName: string): string {
  return buildSmartDocProjectPrefixLabel(projectName);
}

export function buildSmartDocSkillOrTemplateLabel(kind: 'skill' | 'template', name: string): string {
  const type = kind === 'skill' ? '技能' : '模板';
  const n = truncateSmartDocSegment(name, SMART_DOC_SKILL_NAME_DISPLAY_MAX) || type;
  return `@${type}-${wrapSmartDocMentionSegment(n)}`;
}

/**
 * 匹配已完成的行内引用（「」包裹细项，可含空格/连字符）
 * 亦兼容旧版无包裹、无空格写法；项目支持单段（全库）与双段（具体文档）。
 */
export const SMART_DOC_INLINE_MENTION_RE =
  /@(?:技能|模板|模版|模版库|skill|template)-「[^」]+」|@(?:项目|project)-「[^」]+」-「[^」]+」|@(?:项目|project)-「[^」]+」|@(?:技能|模板|模版|模版库|skill|template)-[^\s@「]+|@(?:项目|project)-[^\s@「]+-[^\s@「]+|@(?:项目|project)-[^\s@「]+/gi;
